import { useEffect, useState } from 'react';

const yarnMultipliers = [
  { id: 'sock', label: '4ply / Sock', value: 2.1 },
  { id: 'dk', label: 'DK', value: 1.6 },
  { id: 'aran', label: 'Aran', value: 1.4 },
  { id: 'chunky', label: 'Chunky', value: 1.2 }
];

export default function StartingChainCalculator() {
  const [widthInput, setWidthInput] = useState<number | null>(100);
  const [yarnTypeInput, setYarnTypeInput] = useState<string>('dk');
  const [multiplesInput, setMultiplesInput] = useState<number | null>(1);
  const [additionalsInput, setAdditionalsInput] = useState<number | null>(0);
  const [measurementUnit, setMeasurementUnit] = useState<('cm' | 'in')>('cm');

  const [startingChainLength, setStartingChainLength] = useState<number>(0);

  function handleNumberInput(inputValue: string, setter: React.Dispatch<React.SetStateAction<number | null>>) {
    let inputNumber = parseInt(inputValue);
    if (inputNumber < 0 || isNaN(inputNumber)) {
      return setter(null);
    }
    setter(inputNumber);
  }

  useEffect(() => {
    if (!widthInput) return;
    let widthInCM = widthInput;
    if (measurementUnit === 'in') {
      widthInCM = widthInput * 2.54;
    }

    const multiplier = yarnMultipliers.find((yarn) => yarn.id === yarnTypeInput);
    if (!multiplier) return;

    const startingChain = calculateStartingChain(widthInCM, multiplesInput || 1, additionalsInput || 0, multiplier.value);
    setStartingChainLength(startingChain);
  }, [measurementUnit, yarnTypeInput, widthInput, multiplesInput, additionalsInput]);

  function calculateStartingChain(
    desiredWidthCM: number,
    multipleRequired: number,
    additionalChains: number,
    yarnMultiplier: number
  ) {
    const a = desiredWidthCM * yarnMultiplier;
    const b = Math.ceil(a / multipleRequired) * multipleRequired;
    const c = b + additionalChains;
  
    return c;
  }

  return (
    <>
      <div className='
        tw-bg-white
        tw-border

        tw-shadow-2xl

        sm:tw-m-4
        sm:tw-rounded

        tw-p-4
      '>
        <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-8'>
          <div>
            
            <div className='tw-flex tw-justify-center'>
              <div className='tw-bg-gray-300 tw-rounded tw-p-0.5 tw-text-sm tw-text-gray-800'>
                <button
                  className={`
                    ${measurementUnit === 'cm' ? 'tw-bg-white' : ''}
                    tw-px-2
                    tw-py-1
                    tw-rounded
                  `}
                  onClick={() => setMeasurementUnit('cm')}
                >
                  Centimeters
                </button>
                <button
                  className={`
                    ${measurementUnit === 'in' ? 'tw-bg-white' : ''}
                    tw-px-2
                    tw-py-1
                    tw-rounded
                  `}
                  onClick={() => setMeasurementUnit('in')}
                >
                  Inches
                </button>
              </div>
            </div>

            <div className="tw-mb-4">
              <p className='tw-font-medium tw-text-gray-400'>Yarn Type</p>
              <select
                className='tw-w-full tw-border tw-rounded tw-py-2 tw-px-3 focus:tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-[#6A929C]'
                value={yarnTypeInput}
                onChange={(e) => setYarnTypeInput(e.target.value)}
              >
                {yarnMultipliers.map((multiplier, multiplierIndex) => (
                  <option key={multiplierIndex} value={multiplier.id}>{multiplier.label}</option>
                ))}
              </select>
            </div>

            <div className="tw-mb-4">
              <p className='tw-font-medium tw-text-gray-400'>Desired Width</p>
              <input
                className='tw-w-full tw-border tw-rounded tw-py-2 tw-px-3 focus:tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-[#6A929C]'
                type={'tel'}
                value={widthInput !== null ? widthInput : ''}
                onChange={(e) => handleNumberInput(e.target.value, setWidthInput)}
              />
            </div>

            <div className="tw-mb-4">
              <p className='tw-font-medium tw-text-gray-400'>Multiples Required</p>
              <input
                className='tw-w-full tw-border tw-rounded tw-py-2 tw-px-3 focus:tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-[#6A929C]'
                type={'tel'}
                value={multiplesInput !== null ? multiplesInput : ''}
                onChange={(e) => handleNumberInput(e.target.value, setMultiplesInput)}
              />
            </div>

            <div className="tw-mb-4">
              <p className='tw-font-medium tw-text-gray-400'>Additional Chains</p>
              <input
                className='tw-w-full tw-border tw-rounded tw-py-2 tw-px-3 focus:tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-[#6A929C]'
                type={'tel'}
                value={additionalsInput !== null ? additionalsInput : ''}
                onChange={(e) => handleNumberInput(e.target.value, setAdditionalsInput)}
              />
            </div>

          </div>
          <div>
            <div className="tw-bg-gray-50 tw-p-8 tw-h-full tw-flex tw-flex-col tw-justify-center tw-rounded tw-text-center tw-text-brand-800">
              <h2 className="tw-text-8xl tw-font-sans tw-mb-8">
                {startingChainLength}
              </h2>
              <p className="tw-text-3xl tw-font-thin">Chains</p>
            </div>
          </div>
        </div>
        <div className='tw-flex tw-justify-end tw-mt-2'>
          <p className='tw-text-sm tw-text-gray-300'>
            <a href="https://darylandjames.co.uk" target={'_blank'} rel={'noreferrer'} className='no-underline'>
            Tool developed by &nbsp;
              <span className='tw-font-martel tw-text-gray-300 tw-text-xl tw-group'>
                <span className='group-hover:tw-text-gray-600'>Daryl</span>
                <span className='group-hover:tw-text-gray-400'>&nbsp;&amp;&nbsp;</span>
                <span className='group-hover:tw-text-[#7e8f5c]'>James</span>
              </span>
            </a>
          </p>
        </div>
      </div>
    </>
  );
}